import React, { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Archive, HardHat } from "lucide-react";

import { ListPageLayout } from "@/components/cadastro/ListPageLayout";
import { DataTable, ColumnDef } from "@/components/cadastro/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { WorkforceCache, type Profissao, type Colaborador } from "@/services/workforceCache";

// Helpers
function formatDate(d?: string) {
  try {
    return d ? new Date(d).toLocaleDateString("pt-BR") : "—";
  } catch {
    return "—";
  }
}

export default function MaoDeObra() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Mão de Obra | Cadastro";
    const t = setTimeout(() => setLoading(false), 500);
    
    // Forçar reset do cache para garantir que os 20 colaboradores apareçam
    WorkforceCache.resetToDefaults();
    
    // Recarregar estados após reset
    setProfissoes(WorkforceCache.getProfissoes());
    setColaboradores(WorkforceCache.getColaboradores());
    
    return () => clearTimeout(t);
  }, []);

  // Cache data
  const [profissoes, setProfissoes] = useState<Profissao[]>(() => WorkforceCache.getProfissoes());
  const [colaboradores, setColaboradores] = useState<Colaborador[]>(() => WorkforceCache.getColaboradores());

  // Filters
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("colaboradores");
  const [statusFilter, setStatusFilter] = useState<"todos" | "ativo" | "inativo">("todos");

  // Filter data
  const filteredProfissoes = useMemo(() => {
    let data = [...profissoes];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((p) =>
        `${p.nome} ${p.descricao || ""}`.toLowerCase().includes(q)
      );
    }
    return data;
  }, [profissoes, search]);

  const filteredColaboradores = useMemo(() => {
    let data = [...colaboradores];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((c) =>
        `${c.nome} ${c.profissao} ${c.telefone || ""} ${c.email || ""}`.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "todos") {
      data = data.filter((c) => statusFilter === "ativo" ? c.ativo : !c.ativo);
    }
    return data;
  }, [colaboradores, search, statusFilter]);

  // Modals
  const [editProfissaoOpen, setEditProfissaoOpen] = useState(false);
  const [editColaboradorOpen, setEditColaboradorOpen] = useState(false);
  const [editingProfissao, setEditingProfissao] = useState<Profissao | null>(null);
  const [editingColaborador, setEditingColaborador] = useState<Colaborador | null>(null);

  // Add functions
  function openAddProfissao() {
    setEditingProfissao({ 
      id: "", 
      nome: "", 
      descricao: "", 
      createdAt: new Date().toISOString(), 
      updatedAt: new Date().toISOString() 
    });
    setEditProfissaoOpen(true);
  }

  function openAddColaborador() {
    setEditingColaborador({
      id: "",
      nome: "",
      profissao: "",
      telefone: "",
      email: "",
      endereco: "",
      observacoes: "",
      ativo: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setEditColaboradorOpen(true);
  }

  // Save functions
  function handleSaveProfissao(profissao: Profissao) {
    if (profissao.id) {
      WorkforceCache.updateProfissao(profissao);
    } else {
      WorkforceCache.addProfissao(profissao);
    }
    setProfissoes(WorkforceCache.getProfissoes());
    setEditProfissaoOpen(false);
    toast({ title: "Profissão salva com sucesso!" });
  }

  function handleSaveColaborador(colaborador: Colaborador) {
    if (colaborador.id) {
      WorkforceCache.updateColaborador(colaborador);
    } else {
      WorkforceCache.addColaborador(colaborador);
    }
    setColaboradores(WorkforceCache.getColaboradores());
    setEditColaboradorOpen(false);
    toast({ title: "Colaborador salvo com sucesso!" });
  }

  // Archive functions
  function archiveProfissao(profissao: Profissao) {
    WorkforceCache.deleteProfissao(profissao.id);
    setProfissoes(WorkforceCache.getProfissoes());
    toast({ title: "Profissão arquivada", description: profissao.nome });
  }

  function archiveColaborador(colaborador: Colaborador) {
    WorkforceCache.deleteColaborador(colaborador.id);
    setColaboradores(WorkforceCache.getColaboradores());
    toast({ title: "Colaborador arquivado", description: colaborador.nome });
  }

  // Columns for Profissões
  const profissoesColumns: ColumnDef<Profissao>[] = [
    { 
      key: "nome", 
      header: "Nome", 
      render: (v) => <span className="font-medium">{v}</span>, 
      className: "min-w-[200px]" 
    },
    { 
      key: "descricao", 
      header: "Descrição", 
      render: (v) => v || "—", 
      className: "min-w-[300px]" 
    },
    { 
      key: "updatedAt", 
      header: "Atualizado em", 
      render: (v) => formatDate(v as string), 
      className: "min-w-[140px]" 
    },
    {
      key: "id",
      header: "Ações",
      className: "w-[120px] text-right",
      render: (_v, r) => (
        <div className="flex justify-end gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            aria-label="Editar" 
            onClick={(e) => { 
              e.stopPropagation?.(); 
              setEditingProfissao(r); 
              setEditProfissaoOpen(true); 
            }}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            aria-label="Arquivar" 
            onClick={(e) => { 
              e.stopPropagation?.(); 
              archiveProfissao(r); 
            }}
          >
            <Archive className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Columns for Colaboradores
  const colaboradoresColumns: ColumnDef<Colaborador>[] = [
    { 
      key: "nome", 
      header: "Nome", 
      render: (v) => <span className="font-medium">{v}</span>, 
      className: "min-w-[200px]" 
    },
    { 
      key: "profissao", 
      header: "Profissão", 
      render: (v) => <Badge variant="secondary">{v}</Badge>, 
      className: "min-w-[150px]" 
    },
    { 
      key: "telefone", 
      header: "Telefone", 
      render: (v) => v || "—", 
      className: "min-w-[150px]" 
    },
    { 
      key: "email", 
      header: "E-mail", 
      render: (v) => v ? <a href={`mailto:${v}`} className="underline underline-offset-2">{v}</a> : "—", 
      className: "min-w-[200px]" 
    },
    { 
      key: "ativo", 
      header: "Status", 
      render: (v) => (
        <Badge className={cn("rounded-full", v ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>
          {v ? "Ativo" : "Inativo"}
        </Badge>
      ), 
      className: "min-w-[100px]" 
    },
    { 
      key: "updatedAt", 
      header: "Atualizado em", 
      render: (v) => formatDate(v as string), 
      className: "min-w-[140px]" 
    },
    {
      key: "id",
      header: "Ações",
      className: "w-[120px] text-right",
      render: (_v, r) => (
        <div className="flex justify-end gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            aria-label="Editar" 
            onClick={(e) => { 
              e.stopPropagation?.(); 
              setEditingColaborador(r); 
              setEditColaboradorOpen(true); 
            }}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            aria-label="Arquivar" 
            onClick={(e) => { 
              e.stopPropagation?.(); 
              archiveColaborador(r); 
            }}
          >
            <Archive className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Filters UI for colaboradores
  const colaboradoresFilters = (
    <div className="flex items-center gap-1 rounded-md border p-1">
      <Button variant={statusFilter === "todos" ? "default" : "ghost"} size="sm" className="h-11 sm:h-9" onClick={() => setStatusFilter("todos")}>
        Todos
      </Button>
      <Button variant={statusFilter === "ativo" ? "default" : "ghost"} size="sm" className="h-11 sm:h-9" onClick={() => setStatusFilter("ativo")}>
        Ativos
      </Button>
      <Button variant={statusFilter === "inativo" ? "default" : "ghost"} size="sm" className="h-11 sm:h-9" onClick={() => setStatusFilter("inativo")}>
        Inativos
      </Button>
    </div>
  );

  // Empty states
  const emptyProfissoes = (
    <div className="mt-10 grid place-items-center rounded-lg border border-dashed p-12 text-center">
      <div className="max-w-lg space-y-3">
        <p className="text-lg font-medium">Nenhuma profissão cadastrada</p>
        <p className="text-muted-foreground">Clique em "+ Adicionar" para cadastrar a primeira profissão.</p>
        <Button onClick={openAddProfissao}><Plus className="mr-2 h-4 w-4" /> Adicionar</Button>
      </div>
    </div>
  );

  const emptyColaboradores = (
    <div className="mt-10 grid place-items-center rounded-lg border border-dashed p-12 text-center">
      <div className="max-w-lg space-y-3">
        <p className="text-lg font-medium">Nenhum colaborador cadastrado</p>
        <p className="text-muted-foreground">Clique em "+ Adicionar" para cadastrar o primeiro colaborador.</p>
        <Button onClick={openAddColaborador}><Plus className="mr-2 h-4 w-4" /> Adicionar</Button>
      </div>
    </div>
  );

  return (
    <section className="flex-1">
      <div className="space-y-6">
        <div className="border-b border-border bg-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <HardHat className="h-6 w-6" />
            <h1 className="text-2xl font-semibold">Mão de Obra</h1>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="colaboradores" className="h-11 sm:h-9">Colaboradores</TabsTrigger>
              <TabsTrigger value="profissoes" className="h-11 sm:h-9">Profissões</TabsTrigger>
            </TabsList>

            <TabsContent value="colaboradores" className="mt-6">
              <ListPageLayout
                title="Colaboradores"
                search={search}
                onSearchChange={setSearch}
                onAdd={openAddColaborador}
                filters={colaboradoresFilters}
                searchPlaceholder="Buscar por nome, profissão, telefone ou e-mail…"
              >
                {loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-[44px] w-full" />
                    <Skeleton className="h-[280px] w-full" />
                  </div>
                ) : filteredColaboradores.length === 0 ? (
                  emptyColaboradores
                ) : (
                  <DataTable data={filteredColaboradores} setData={setColaboradores} columns={colaboradoresColumns} pageSize={8} dense />
                )}
              </ListPageLayout>
            </TabsContent>

            <TabsContent value="profissoes" className="mt-6">
              <ListPageLayout
                title="Profissões"
                search={search}
                onSearchChange={setSearch}
                onAdd={openAddProfissao}
                searchPlaceholder="Buscar por nome ou descrição…"
              >
                {loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-[44px] w-full" />
                    <Skeleton className="h-[280px] w-full" />
                  </div>
                ) : filteredProfissoes.length === 0 ? (
                  emptyProfissoes
                ) : (
                  <DataTable data={filteredProfissoes} setData={setProfissoes} columns={profissoesColumns} pageSize={8} dense />
                )}
              </ListPageLayout>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Profissão Edit/Create Modal */}
      <Dialog open={editProfissaoOpen} onOpenChange={setEditProfissaoOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingProfissao?.id ? "Editar Profissão" : "Nova Profissão"}</DialogTitle>
          </DialogHeader>
          {editingProfissao && (
            <ProfissaoForm 
              initial={editingProfissao} 
              onCancel={() => setEditProfissaoOpen(false)} 
              onSave={handleSaveProfissao} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Colaborador Edit/Create Modal */}
      <Dialog open={editColaboradorOpen} onOpenChange={setEditColaboradorOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingColaborador?.id ? "Editar Colaborador" : "Novo Colaborador"}</DialogTitle>
          </DialogHeader>
          {editingColaborador && (
            <ColaboradorForm 
              initial={editingColaborador} 
              profissoes={profissoes}
              onCancel={() => setEditColaboradorOpen(false)} 
              onSave={handleSaveColaborador} 
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Profissão Form Component
function ProfissaoForm({ 
  initial, 
  onSave, 
  onCancel 
}: { 
  initial: Profissao; 
  onSave: (p: Profissao) => void; 
  onCancel: () => void; 
}) {
  const [form, setForm] = useState<Profissao>(initial);

  function validate(): string | null {
    if (!form.nome?.trim()) return "Nome é obrigatório";
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="nome">Nome *</Label>
        <Input
          id="nome"
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          placeholder="Ex: Pedreiro, Eletricista, etc."
        />
      </div>
      <div>
        <Label htmlFor="descricao">Descrição</Label>
        <Textarea
          id="descricao"
          value={form.descricao || ""}
          onChange={(e) => setForm({ ...form, descricao: e.target.value })}
          placeholder="Descreva as responsabilidades desta profissão..."
          rows={3}
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={() => {
          const err = validate();
          if (err) return toast({ title: err, variant: "destructive" });
          onSave(form);
        }}>
          Salvar
        </Button>
      </DialogFooter>
    </div>
  );
}

// Colaborador Form Component
function ColaboradorForm({ 
  initial, 
  profissoes,
  onSave, 
  onCancel 
}: { 
  initial: Colaborador; 
  profissoes: Profissao[];
  onSave: (c: Colaborador) => void; 
  onCancel: () => void; 
}) {
  const [form, setForm] = useState<Colaborador>(initial);

  function validate(): string | null {
    if (!form.nome?.trim()) return "Nome é obrigatório";
    if (!form.profissao?.trim()) return "Profissão é obrigatória";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "E-mail inválido";
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="nome">Nome *</Label>
          <Input
            id="nome"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            placeholder="Nome completo"
          />
        </div>
        <div>
          <Label htmlFor="profissao">Profissão *</Label>
          <Input
            id="profissao"
            value={form.profissao}
            onChange={(e) => setForm({ ...form, profissao: e.target.value })}
            placeholder="Ex: Pedreiro, Eletricista"
            list="profissoes-list"
          />
          <datalist id="profissoes-list">
            {profissoes.map((p) => (
              <option key={p.id} value={p.nome} />
            ))}
          </datalist>
        </div>
        <div>
          <Label htmlFor="telefone">Telefone</Label>
          <Input
            id="telefone"
            value={form.telefone || ""}
            onChange={(e) => setForm({ ...form, telefone: e.target.value })}
            placeholder="(00) 00000-0000"
          />
        </div>
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type="email"
            value={form.email || ""}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="email@exemplo.com"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="endereco">Endereço</Label>
          <Input
            id="endereco"
            value={form.endereco || ""}
            onChange={(e) => setForm({ ...form, endereco: e.target.value })}
            placeholder="Cidade, Estado"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="observacoes">Observações</Label>
          <Textarea
            id="observacoes"
            value={form.observacoes || ""}
            onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
            placeholder="Observações adicionais sobre o colaborador..."
            rows={3}
          />
        </div>
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ativo"
              checked={form.ativo}
              onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="ativo">Colaborador ativo</Label>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={() => {
          const err = validate();
          if (err) return toast({ title: err, variant: "destructive" });
          onSave(form);
        }}>
          Salvar
        </Button>
      </DialogFooter>
    </div>
  );
}